// Selling a membership in the office (memberships.sell; office-history prototype's "Offer the
// membership"): a plan, the location it covers, and the open job's draft invoice it goes on, as the
// action requires. Its revenue is deferred and its included visits come due for the office to book.
// Opened from the customer record or from Memberships with openSellMembership(customerId?).
// On ui-web's FormSheet (patterns/README.md, BL-189): its fields are the catalogued ones, the customer
// found with MatchList, the plan's facts Figures; once sold it stays open on what was done (a done
// Notice, Close alone), so the sentence saying where its line went is read before it closes.

import { useEffect, useRef, useState } from 'react';
import { Checkbox, Figures, MatchList, money, Notice, SelectField, TextField } from '@flowology/ui-web/fl';
import { FormSheet } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { errorText } from '../books/ui.tsx';
import { newId } from '../shared/ids.ts';
import { useSaveKey } from '../shared/save-key.ts';
import type { Customer } from '../types.ts';
import {
  includedText,
  renewalConsent,
  sellable,
  termText,
  type DraftInvoice,
  type Plan,
  type SellablePlan,
  type Renewal,
  type RenewalTerms,
} from './model.ts';
import { closeSlot, onLeave, openSlot, type Slot } from '../shared/sheet-slot.ts';
import { membershipSold } from './sold.ts';
import { TermsAgreed } from './member-parts.tsx';
import './memberships.css';

let opener: ((customerId: string | null) => void) | null = null;

/** Opens the sell dialog, for a customer or to find one. A no-op until the app has mounted the host. */
export function openSellMembership(customerId?: string): void {
  opener?.(customerId ?? null);
}

/** Mounted once by the app. */
export function SellMembershipHost({ today, onSold }: { today: string; onSold?: () => void }) {
  // Each opening is its own: a dialog closing late never shuts the one opened again at once.
  const [open, setOpen] = useState<Slot<string | null> | null>(null);
  useEffect(() => {
    opener = (customerId) => setOpen(openSlot(customerId));
    // leaving the page it's over (Back, a link) closes it: the view follows the address
    const stop = onLeave(window, () => setOpen(null));
    return () => {
      opener = null;
      stop();
    };
  }, []);
  if (!open) return null;
  return (
    <SellDialog
      key={open.at}
      customerId={open.value}
      today={today}
      onClose={() => setOpen((cur) => closeSlot(cur, open.at))}
      onSold={() => {
        membershipSold(); // Members and the like read again (QA-531)
        onSold?.();
      }}
    />
  );
}

interface JobGet {
  id: string;
  number: string;
  summary: string;
  locationId: string;
  address: string;
  invoice: { id: string; number: string; status: string } | null;
}

function SellDialog({
  customerId: given,
  today,
  onClose,
  onSold,
}: {
  customerId: string | null;
  today: string;
  onClose: () => void;
  onSold: () => void;
}) {
  const [customerId, setCustomerId] = useState(given);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [drafts, setDrafts] = useState<DraftInvoice[] | null>(null);
  const [plans, setPlans] = useState<SellablePlan[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [planId, setPlanId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [startsOn, setStartsOn] = useState(today);
  const [firstVisitOn, setFirstVisitOn] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  // Renewing by itself needs the customer's yes to the terms shown, word for word (F-27)
  const [terms, setTerms] = useState<RenewalTerms | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [sold, setSold] = useState<string | null>(null);
  // One membership id per attempt: a lost answer retried sells it once (the action replays by id).
  const [membershipId] = useState(newId);
  const save = useSaveKey(
    `${membershipId}:${planId}:${invoiceId}:${locationId}:${startsOn}:${firstVisitOn}:${autoRenew}:${agreed ? terms?.version : ''}`,
  );

  // the plan's renewal terms, read when renewing is ticked (and again for another plan)
  useEffect(() => {
    setTerms(null);
    setAgreed(false);
    setTermsError(null);
    if (!autoRenew || !planId) return;
    let current = true;
    client
      .call<RenewalTerms>('memberships.renewalTerms', { typeId: planId })
      .then((t) => current && setTerms(t))
      .catch((e: unknown) => current && setTermsError(errorText(e, 'Could not load the renewal terms.')));
    return () => {
      current = false;
    };
  }, [autoRenew, planId]);

  useEffect(() => {
    client
      .call<{ plans: Plan[] }>('membershipTypes.list', {})
      .then((r) => {
        // a plan imported without its term, visits or price isn't sold until they're set
        const live = r.plans.filter((p) => p.active).filter(sellable);
        setPlans(live);
        if (live.length === 1) setPlanId(live[0]!.id);
      })
      .catch((e: unknown) => setLoadError(errorText(e, 'Could not load the plans.')));
  }, []);

  useEffect(() => {
    if (!customerId) return;
    setCustomer(null);
    setDrafts(null);
    void (async () => {
      try {
        const [c, js] = await Promise.all([
          client.call<Customer>('customers.get', { id: customerId }),
          client.call<{ jobs: { id: string }[] }>('jobs.list', {
            customerId,
            status: ['open', 'in_progress', 'awaiting_review'],
            limit: 10,
          }),
        ]);
        setCustomer(c);
        const jobs = await Promise.all(js.jobs.map((j) => client.call<JobGet>('jobs.get', { id: j.id })));
        const open = jobs
          .filter((j) => j.invoice?.status === 'draft')
          .map((j) => ({
            invoiceId: j.invoice!.id,
            invoiceNumber: j.invoice!.number,
            jobId: j.id,
            jobNumber: j.number,
            summary: j.summary,
            locationId: j.locationId,
            address: j.address,
          }));
        setDrafts(open);
        if (open[0]) {
          setInvoiceId(open[0].invoiceId);
          setLocationId(open[0].locationId);
        } else setLocationId(c.locations[0]?.id ?? '');
      } catch (e) {
        setLoadError(errorText(e, 'Could not load the customer.'));
      }
    })();
  }, [customerId]);

  const plan = plans?.find((p) => p.id === planId);
  const renewal = renewalConsent(autoRenew, terms, agreed);
  const ready = !!customer && !!plan && !!invoiceId && !!locationId && !!startsOn && renewal.ready;
  // FormSheet closes once its submit resolves: just sold, it stays open on what was done instead
  const justSold = useRef(false);
  const sell = async () => {
    if (!ready || !customer) return;
    let r: { renewal?: Renewal };
    try {
      r = await client.call<{ renewal?: Renewal }>(
        'memberships.sell',
        {
          membershipId,
          typeId: planId,
          customerId: customer.id,
          locationId,
          invoiceId,
          startsOn,
          ...(firstVisitOn ? { firstVisitOn } : {}),
          autoRenew,
          ...(renewal.consent ? { renewalConsent: renewal.consent } : {}),
        },
        { idempotencyKey: save.key },
      );
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    }
    const inv = drafts?.find((x) => x.invoiceId === invoiceId);
    justSold.current = true;
    setSold(
      `${customer.displayName} is now on ${plan!.name}. Its line is on invoice ${inv?.invoiceNumber ?? ''}${plan!.visitsPerTerm ? '; the included visits come due in To book' : ''}.${r.renewal?.notice ? ` ${r.renewal.notice}` : ''}`,
    );
    onSold();
  };
  const close = () => {
    if (justSold.current) {
      justSold.current = false;
      return;
    }
    onClose();
  };
  const title = customer ? `Offer a membership to ${customer.displayName}` : 'Sell a membership';

  if (sold)
    return (
      <FormSheet isOpen title={title} isDirty={false} isReadOnly onClose={close} onSubmit={() => undefined}>
        <Notice tone="done" title={sold} takesFocus />
      </FormSheet>
    );
  return (
    <FormSheet
      isOpen
      title={title}
      line="It goes on an open job’s draft invoice. The revenue is spread over the term, visit by visit."
      submitLabel="Sell the membership"
      isDirty={false}
      isSubmitDisabled={!ready}
      onClose={close}
      onSubmit={sell}
    >
      {loadError && (
        <p className="bp-member-bad" role="alert">
          {loadError}
        </p>
      )}
      {!customerId && <FindCustomer onPick={setCustomerId} />}
      {customerId && !customer && !loadError && <p className="bp-member-quiet">Loading the customer…</p>}
      {customer && plans && (
        <>
          <SelectField
            label="Plan"
            value={planId}
            onChange={setPlanId}
            options={[
              { value: '', label: 'Pick a plan…' },
              ...plans.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
          {plans.length === 0 && (
            <Notice title="There are no plans to sell yet.">
              The owner or a manager adds them in Plans.
            </Notice>
          )}
          {plan && (
            <Figures
              label="The plan"
              items={[
                { label: 'Price', value: money(plan.priceCents) },
                { label: 'Term', value: termText(plan.termMonths) },
                { label: 'Includes', value: includedText(plan.visitsPerTerm) },
              ]}
            />
          )}
          {drafts === null ? (
            <p className="bp-member-quiet">Loading the open jobs…</p>
          ) : drafts.length === 0 ? (
            <Notice title={`${customer.displayName} has no open job with a draft invoice.`}>
              Book a visit first; the membership goes on its invoice.
            </Notice>
          ) : (
            <SelectField
              label="On the invoice of"
              value={invoiceId}
              onChange={(id) => {
                setInvoiceId(id);
                const d = drafts.find((x) => x.invoiceId === id);
                if (d) setLocationId(d.locationId);
              }}
              options={drafts.map((d) => ({
                value: d.invoiceId,
                label: `Job #${d.jobNumber} · ${d.summary.slice(0, 60)} · invoice ${d.invoiceNumber}`,
              }))}
            />
          )}
          {customer.locations.length > 1 && (
            <SelectField
              label="The location it covers"
              value={locationId}
              onChange={setLocationId}
              options={customer.locations.map((l) => ({
                value: l.id,
                label: `${l.name ? `${l.name} · ` : ''}${l.line1}, ${l.city}`,
              }))}
            />
          )}
          <div className="bp-member-pair">
            <TextField type="date" width="date" label="Starts" value={startsOn} onChange={setStartsOn} />
            {plan && plan.visitsPerTerm > 0 && (
              <TextField
                type="date"
                width="date"
                label="First visit due (optional)"
                value={firstVisitOn}
                min={startsOn}
                onChange={setFirstVisitOn}
              />
            )}
          </div>
          {plan && plan.visitsPerTerm > 0 && !firstVisitOn && (
            <p className="bp-member-quiet">The first visit comes due on {dayText(startsOn)}.</p>
          )}
          <Checkbox isSelected={autoRenew} onChange={setAutoRenew}>
            Renew automatically each term (the customer agrees to the terms below)
          </Checkbox>
          {autoRenew && <TermsAgreed terms={terms} error={termsError} agreed={agreed} onAgree={setAgreed} />}
          {customer.doNotContact && (
            <p className="bp-member-quiet">
              {customer.displayName} is do-not-contact: no welcome or renewal messages go out. Call them
              instead.
            </p>
          )}
        </>
      )}
    </FormSheet>
  );
}

function FindCustomer({ onPick }: { onPick: (id: string) => void }) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ id: string; displayName: string; city: string | null }[]>([]);
  useEffect(() => {
    if (!query.trim()) return setFound([]);
    const t = setTimeout(() => {
      client
        .call<{ customers: { id: string; displayName: string; city: string | null }[] }>('customers.list', {
          query: query.trim(),
          limit: 8,
        })
        .then((r) => setFound(r.customers))
        .catch(() => setFound([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);
  return (
    <div className="bp-member-find">
      <TextField
        type="search"
        label="Customer"
        placeholder="Name, number or street"
        value={query}
        onChange={setQuery}
        autoComplete="off"
        combobox={{ expanded: found.length > 0, controls: 'sell-found' }}
      />
      {found.length > 0 && (
        <MatchList
          id="sell-found"
          label="Customers"
          items={found.map((c) => ({ id: c.id, name: c.displayName, detail: c.city }))}
          onPick={onPick}
        />
      )}
    </div>
  );
}
